import React, { Component } from 'react';
import { injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import styleable from 'react-styleable';
import toLower from 'lodash/toLower';
import XLSX from 'xlsx/dist/xlsx.core.min';
import cloneDeep from 'lodash/cloneDeep';
import DataGrid from '../../../components/data-grid/index';
import messages from '../messages';
import css from '../index.scss';
import { RollOverSummaryRenderer } from '../../../components/renderers/index';
import { createRolloverSummaryRows } from '../../../helpers/grid-util';
import { DARK } from '../../button/variants';
import Button from '../../button/index';
import DropdownList from '../../dropdown-list/index';
import PortalCommunication from '../../../portal-comms';
import { Download } from '../../../helpers/icon-dictionary';
import Modal from '../../modal';
import SignInCardsFormats from '../../sign-in-cards-formats';
import { getLocale } from '../../../../app/i18n';

function getGridColumns(intl, filterType = 'newStudents') {
    let gridColumns = [];
    switch (filterType) {
    case 'newStudents':
    case 'updatedStudents':
    case 'transferredStudents':
        gridColumns = [
            {
                key: 'firstName',
                name: `${intl.formatMessage(messages.firstName)}`
            },
            {
                key: 'surname',
                name: `${intl.formatMessage(messages.surname)}`
            },
            {
                key: 'yearGroup',
                name: `${intl.formatMessage(messages.year)}`,
                width: 70
            },
            {
                key: 'classroomName',
                name: `${intl.formatMessage(messages.className)}`
            },
            {
                key: 'username',
                name: `${intl.formatMessage(messages.username)}`
            },
            {
                key: 'password',
                name: `${intl.formatMessage(messages.password)}`
            }
        ];
        break;

    case 'unassignedStudents':
        gridColumns = [
            {
                key: 'firstName',
                name: `${intl.formatMessage(messages.firstName)}`
            },
            {
                key: 'surname',
                name: `${intl.formatMessage(messages.surname)}`
            },
            {
                key: 'username',
                name: `${intl.formatMessage(messages.username)}`
            },
            {
                key: 'password',
                name: `${intl.formatMessage(messages.password)}`
            }
        ];
        break;

    case 'newTeachers':
    case 'updatedTeachers':
        gridColumns = [
            {
                key: 'title',
                name: `${intl.formatMessage(messages.title)}`,
                width: 70
            },
            {
                key: 'firstName',
                name: `${intl.formatMessage(messages.firstName)}`
            },
            {
                key: 'surname',
                name: `${intl.formatMessage(messages.surname)}`
            },
            {
                key: 'email',
                name: `${intl.formatMessage(messages.teacherEmail)}`
            },
            {
                key: 'username',
                name: `${intl.formatMessage(messages.username)}`
            }
        ];
        break;

    case 'newClassrooms':
    case 'updatedClassrooms':
        gridColumns = [
            {
                key: 'classroomName',
                name: `${intl.formatMessage(messages.className)}`
            },
            {
                key: 'teacherName',
                name: `${intl.formatMessage(messages.teacherName)}`
            },
            {
                key: 'yearGroup',
                name: `${intl.formatMessage(messages.year)}`,
                width: 80
            },
            {
                key: 'assignedStudents',
                name: `${intl.formatMessage(messages.assignedStudents)}`,
                width: 200
            }
        ];
        break;

    default:
        gridColumns = [];
        break;
    }
    return gridColumns;
}

class RollOverSummary extends Component {
    constructor(props) {
        super(props);

        const { rolloverSummary } = this.props;

        this.filterType = 'newStudents';
        this.rows = createRolloverSummaryRows(rolloverSummary);
        this.state = { rows: this.rows };
        this.onFilterDropdown = this.onFilterDropdown.bind(this);
        this.dropDownOptions = this.dropDownOptions.bind(this);
        this.getCurrentSelection = this.getCurrentSelection.bind(this);
        this.onPrintSignInCards = this.onPrintSignInCards.bind(this);
        this.writeToExcel = this.writeToExcel.bind(this);
    }

    onFilterDropdown(name, value) {
        this.filterType = value;
        const { rolloverSummary } = this.props;
        this.rows = createRolloverSummaryRows(rolloverSummary, value);
        this.setState({ rows: this.rows });
    }

    getCurrentSelection() {
        const { intl, rolloverSummary } = this.props;
        let user = {};
        switch (this.filterType) {
        case 'newStudents':
            user = {
                downloadLabel: intl.formatMessage(messages.theNewStudent),
                userType: toLower(intl.formatMessage(messages.newStudents)),
                length: rolloverSummary[this.filterType].length
            };
            break;

        case 'updatedStudents':
            user = {
                downloadLabel: intl.formatMessage(messages.theUpdatedStudent),
                userType: toLower(intl.formatMessage(messages.updatedStudents)),
                length: rolloverSummary[this.filterType].length
            };
            break;

        case 'unassignedStudents':
            user = {
                downloadLabel: intl.formatMessage(messages.theUnassignedStudent),
                userType: toLower(intl.formatMessage(messages.unassignedStudents)),
                length: rolloverSummary[this.filterType].length
            };
            break;
        case 'transferredStudents':
            user = {
                downloadLabel: intl.formatMessage(messages.theTransferredStudent),
                userType: toLower(intl.formatMessage(messages.transferredStudents)),
                length: rolloverSummary[this.filterType].length
            };
            break;

        case 'newTeachers':
            user = {
                downloadLabel: intl.formatMessage(messages.theNewTeacher),
                userType: toLower(intl.formatMessage(messages.newTeachers)),
                length: rolloverSummary[this.filterType].length
            };
            break;

        case 'updatedTeachers':
            user = {
                downloadLabel: intl.formatMessage(messages.theUpdatedTeacher),
                userType: toLower(intl.formatMessage(messages.updatedTeachers)),
                length: rolloverSummary[this.filterType].length
            };
            break;

        case 'unassignedTeachers':
            user = {
                downloadLabel: intl.formatMessage(messages.theUnassignedTeacher),
                userType: toLower(intl.formatMessage(messages.unassignedTeachers)),
                length: rolloverSummary[this.filterType].length
            };
            break;

        case 'newClassrooms':
            user = {
                downloadLabel: intl.formatMessage(messages.theNewClasses),
                userType: toLower(intl.formatMessage(messages.newClasses)),
                length: rolloverSummary[this.filterType].length
            };
            break;

        case 'updatedClassrooms':
            user = {
                downloadLabel: intl.formatMessage(messages.theUpdatedClasses),
                userType: toLower(intl.formatMessage(messages.updatedClasses)),
                length: rolloverSummary[this.filterType].length
            };
            break;

        default:
            break;
        }
        return user;
    }

    dropDownOptions() {
        const { intl, rolloverSummary } = this.props;
        return ([{
            key: 'newStudents',
            value: `${intl.formatMessage(messages.newStudents)} (${rolloverSummary.newStudents.length})`,
            selected: true
        }, {
            key: 'updatedStudents',
            value: `${intl.formatMessage(messages.updatedStudents)} (${rolloverSummary.updatedStudents.length})`,
            selected: false
        }, {
            key: 'unassignedStudents',
            value: `${intl.formatMessage(messages.unassignedStudents)} (${rolloverSummary.unassignedStudents.length})`,
            selected: false
        }, {
            key: 'transferredStudents',
            value: `${intl.formatMessage(messages.transferredStudents)} (${rolloverSummary.transferredStudents.length})`,
            selected: false
        }, {
            key: 'newTeachers',
            value: `${intl.formatMessage(messages.newTeachers)} (${rolloverSummary.newTeachers.length})`,
            selected: false
        }, {
            key: 'updatedTeachers',
            value: `${intl.formatMessage(messages.updatedTeachers)} (${rolloverSummary.updatedTeachers.length})`,
            selected: false
        }, {
            key: 'newClassrooms',
            value: `${intl.formatMessage(messages.newClasses)} (${rolloverSummary.newClassrooms.length})`,
            selected: false
        }, {
            key: 'updatedClassrooms',
            value: `${intl.formatMessage(messages.updatedClasses)} (${rolloverSummary.updatedClassrooms.length})`,
            selected: false
        }]);
    }

    onPrintSignInCards(format) {
        const { selectedProduct } = this.props;
        const userIds = this.rows.map(row => row.id);
        const locale = getLocale();

        switch (this.filterType) {
        case 'newStudents':
        case 'updatedStudents':
        case 'unassignedStudents':
        case 'transferredStudents':
            this.props.onRollOverDownloadSignInCardRequest(selectedProduct.id, userIds, 'student', format, locale);
            break;

        case 'newTeachers':
        case 'updatedTeachers':
            this.props.onRollOverDownloadSignInCardRequest(selectedProduct.id, userIds, 'teacher', 'QR_3X3', locale);
            break;

        case 'updatedClassrooms':
        case 'newClassrooms':
            this.props.onRollOverDownloadSignInCardRequest(selectedProduct.id, userIds, 'classroom', format, locale);
            break;

        default:
            break;
        }

        this.props.showHideSignInCardModalRequest(false);
    }

    writeToExcel() {
        const { intl, rolloverSummary } = this.props;

        let sheetHeaders = [];
        let downloadingFileName = '';

        switch (this.filterType) {
        case 'newStudents':
            sheetHeaders = [
                intl.formatMessage(messages.firstName),
                intl.formatMessage(messages.surname),
                intl.formatMessage(messages.year),
                intl.formatMessage(messages.className),
                intl.formatMessage(messages.username),
                intl.formatMessage(messages.password)
            ];
            downloadingFileName = intl.formatMessage(messages.newStudentsFile);
            break;

        case 'updatedStudents':
            sheetHeaders = [
                intl.formatMessage(messages.firstName),
                intl.formatMessage(messages.surname),
                intl.formatMessage(messages.year),
                intl.formatMessage(messages.className),
                intl.formatMessage(messages.username),
                intl.formatMessage(messages.password)
            ];
            downloadingFileName = intl.formatMessage(messages.updatedStudentsFile);
            break;

        case 'unassignedStudents':
            sheetHeaders = [
                intl.formatMessage(messages.firstName),
                intl.formatMessage(messages.surname),
                intl.formatMessage(messages.username),
                intl.formatMessage(messages.password)
            ];
            downloadingFileName = intl.formatMessage(messages.unassignedStudentsFile);
            break;

        case 'transferredStudents':
            sheetHeaders = [
                intl.formatMessage(messages.firstName),
                intl.formatMessage(messages.surname),
                intl.formatMessage(messages.year),
                intl.formatMessage(messages.className),
                intl.formatMessage(messages.username),
                intl.formatMessage(messages.password)
            ];
            downloadingFileName = intl.formatMessage(messages.transferredStudentsFile);
            break;

        case 'newTeachers':
            sheetHeaders = [
                intl.formatMessage(messages.title),
                intl.formatMessage(messages.firstName),
                intl.formatMessage(messages.surname),
                intl.formatMessage(messages.teacherEmail),
                intl.formatMessage(messages.username)
            ];
            downloadingFileName = intl.formatMessage(messages.newTeachersFile);
            break;

        case 'updatedTeachers':
            sheetHeaders = [
                intl.formatMessage(messages.title),
                intl.formatMessage(messages.firstName),
                intl.formatMessage(messages.surname),
                intl.formatMessage(messages.teacherEmail),
                intl.formatMessage(messages.username)
            ];
            downloadingFileName = intl.formatMessage(messages.updatedTeachersFile);
            break;

        case 'newClassrooms':
            sheetHeaders = [
                intl.formatMessage(messages.className),
                intl.formatMessage(messages.teacherName),
                intl.formatMessage(messages.year),
                intl.formatMessage(messages.assignedStudents)
            ];
            downloadingFileName = intl.formatMessage(messages.newClassroomsFile);
            break;

        case 'updatedClassrooms':
            sheetHeaders = [
                intl.formatMessage(messages.className),
                intl.formatMessage(messages.teacherName),
                intl.formatMessage(messages.year),
                intl.formatMessage(messages.assignedStudents)
            ];
            downloadingFileName = intl.formatMessage(messages.updatedClassroomsFile);
            break;

        default:
            sheetHeaders = [];
            break;
        }
        // Clone the result from rolloversummary
        const sheetData = cloneDeep(rolloverSummary[this.filterType]);
        const results = sheetData.map((item) => {
            const isUnassignedStudents = ['unassignedStudents'].indexOf(this.filterType) !== -1;
            let newItem = [];
            delete item.id;
            delete item.index;
            if (isUnassignedStudents) { // FE fix the ordering of data columns for teachers
                const { firstName, surname, username, password } = item;
                newItem = [...newItem, firstName, surname, username, password];
            } else {
                for (const [key] of Object.entries(item)) {
                    if (this.filterType.indexOf('Teachers') !== -1 && key !== 'password') {
                        newItem.push(item[key]);
                    } else if (this.filterType.indexOf('Teachers') === -1) {
                        newItem.push(item[key]);
                    }
                }
            }

            return newItem;
        });
        // Add headers
        const worksheet = XLSX.utils.aoa_to_sheet([
            sheetHeaders
        ]);
        // Append data
        XLSX.utils.sheet_add_aoa(worksheet, results, { origin: 1, skipHeader: true });
        // Create a blank workbook using SheetJS
        const workbook = XLSX.utils.book_new();
        // Append the JSON sheet
        XLSX.utils.book_append_sheet(workbook, worksheet);

        XLSX.writeFile(workbook, downloadingFileName);
    }

    render() {
        const { intl, isDownloadingRolloverSignInCards, rolloverSummary } = this.props;
        const rowGetter = rowNumber => this.rows[rowNumber];
        const dataGridClasses = [css.dataGridContainer, css.summaryDatagrid].join(' ');
        const summaryContentClasses = [css.contentWrapper, css.summaryContentWrapper].join(' ');
        const exportBtnIsDisabled = rolloverSummary[this.filterType].length === 0;
        return (
            <div>
                <div className={css.rollOverComponent}>
                    <div className={css.header}>
                        <div className={css.title}>{intl.formatMessage(messages.header)}</div>
                    </div>
                    <div className={css.content}>
                        <div className={summaryContentClasses}>
                            <div className={css.dataGridWrapper}>
                                <div className={css.summaryWrapper}>
                                    <div className={css.summary}>{intl.formatMessage(messages.summary)}</div>
                                    <span className={css.dropdownBorder}>
                                        <DropdownList
                                            onChange={this.onFilterDropdown}
                                            options={this.dropDownOptions()}
                                        />
                                    </span>
                                </div>
                                <div className={dataGridClasses}>
                                    <DataGrid
                                        columns={getGridColumns(intl, this.filterType)}
                                        rowGetter={rowGetter}
                                        rowKey="id"
                                        rowRenderer={RollOverSummaryRenderer}
                                        rowsCount={this.rows.length}
                                        rowSelectionEnabled={false}
                                        selectedRows={[]}
                                        showSelectionColumn={false} />
                                </div>
                            </div>
                        </div>
                        <div className={css.rollOverFooter}>
                            <div className={css.rollOverFooterRight}>
                                <Button
                                    handleOnClick={() => PortalCommunication.navigateToLandingScreen('Rollover')}
                                    label={intl.formatMessage(messages.done)}
                                    variant={DARK} />
                            </div>
                            <div className={css.rollOverFooterLeft}>
                                {
                                    this.getCurrentSelection().downloadLabel.indexOf('teacher') !== -1 ?
                                        (<Button
                                            disabled={this.getCurrentSelection().length === 0}
                                            handleOnClick={() => this.onPrintSignInCards('QR_3X3')}
                                            hidden={this.getCurrentSelection().userType === 'classes'}
                                            icon={Download}
                                            label={` ${intl.formatMessage(messages.download)} ${this.getCurrentSelection().downloadLabel}`}
                                            loading={isDownloadingRolloverSignInCards}
                                            loadingLabel={this.props.intl.formatMessage(messages.downloading)}
                                            variant={DARK} />) :
                                        (<Button
                                            disabled={this.getCurrentSelection().length === 0}
                                            handleOnClick={() => this.props.showHideSignInCardModalRequest(true)}
                                            hidden={this.getCurrentSelection().userType === 'classes'}
                                            icon={Download}
                                            label={` ${intl.formatMessage(messages.download)} ${this.getCurrentSelection().downloadLabel}`}
                                            variant={DARK} />)
                                }
                                <Button
                                    disabled={exportBtnIsDisabled}
                                    handleOnClick={this.writeToExcel}
                                    label={intl.formatMessage(messages.exportSummary)}
                                    variant={DARK} />
                            </div>
                        </div>
                    </div>
                </div>
                <Modal show={this.props.showHideSignInCardModal}>
                    <SignInCardsFormats
                        handleOnCancel={() => this.props.showHideSignInCardModalRequest(false)}
                        handleOnDownloadSignInCards={format => this.onPrintSignInCards(format)}
                        isSignInCardDownloaded={!isDownloadingRolloverSignInCards} />
                </Modal>
            </div>
        );
    }
}

RollOverSummary.propTypes = {
    intl: intlShape.isRequired,
    rolloverSummary: PropTypes.object,
    onRollOverDownloadSignInCardRequest: PropTypes.func,
    isDownloadingRolloverSignInCards: PropTypes.bool,
    selectedProduct: PropTypes.object,
    showHideSignInCardModal: PropTypes.bool,
    showHideSignInCardModalRequest: PropTypes.func
};


export default injectIntl(styleable(css)(RollOverSummary));



// WEBPACK FOOTER //
// ./app/components/roll-over/pages/summary.js